import {
  readInteger,
  cart
} from "./chunk-jwpq5e39.js";
import {
  fetchSectionParts,
  fetchRecommendationsSection,
  upgradeAttributes,
  clearCartError,
  showCartError
} from "./chunk-9v5gng0d.js";
import {
  readUpgradeOffer,
  readUpgradedLine,
  trackPromotion,
  readRecoTile,
  trackRecoSelect,
  trackRecoAdd,
  watchCartImpressions,
  hsfChildLinesOf,
  reconcileHsfLines,
  readHsfTwins,
  confirmRemoval,
  setLineQuantity,
  carriedProperties,
  swapLines,
  addRecommendedLine,
  applyUpgrade,
  undoUpgrade,
  disarmLegacyCartForm
} from "./chunk-9jtvqyp8.js";
import {
  selectOnly,
  arrowNavigate
} from "./chunk-zpd9q9kk.js";
import {
  subscriptionTypeFor,
  captureFocus
} from "./chunk-fn5j2c34.js";

// src/pages/cart-v2/attributes.ts
var cartPageAttributes = {
  add: "data-cart-v2-add",
  addDiscount: "data-cart-v2-add-discount",
  addGlobalId: "data-cart-v2-add-global-id",
  addMode: "data-cart-v2-add-mode",
  recoOption: "data-cart-v2-reco-option",
  addPlan: "data-cart-v2-add-plan",
  count: "data-cart-v2-count",
  isLoading: "data-is-loading",
  legacy: "data-cart-v2-legacy",
  limit: "data-limit",
  lineKey: "data-line-key",
  main: "data-cart-v2-main",
  productId: "data-product-id",
  quantity: "data-cart-v2-quantity",
  rail: "data-cart-v2-rail",
  restoring: "data-cart-v2-restoring",
  welcome: "data-cart-v2-welcome",
  recommendationsPerformed: "data-cart-v2-recommendations-performed",
  shareModal: "data-share-cart-modal",
  swap: "data-cart-v2-swap",
  swapChildren: "data-swap-children",
  swapMode: "data-swap-mode",
  swapPlan: "data-swap-plan",
  swapPlanName: "data-swap-plan-name",
  swapGlobalId: "data-swap-global-id",
  swapTotal: "data-swap-total",
  swapVariant: "data-swap-variant"
};

// src/pages/cart-v2/share.ts
var isReceivedSharedCart = () => (new URLSearchParams(window.location.search).get("cart_token") ?? "").trim() !== "";

// src/pages/cart-v2/page.ts
var RESTORE_TIMEOUT_MS = 30000;
var SECTION_ID = "cart-page-v2";
var COUNT_ID = "cart-page-v2-count";
var MAIN_ID = "cart-page-v2-main";
var RAIL_ID = "cart-page-v2-rail";
var WELCOME_ID = "cart-page-v2-welcome";
var RECOMMENDATIONS_KEY_ID = "cart-page-v2-reco-key";
var RECOMMENDATIONS_OPTION_ID = "cart-page-v2-reco-option";
var RECOMMENDATIONS_SECTION_ID = "cart-page-v2-recommendations";
var RECOMMENDATIONS_SELECTOR = `[${cartPageAttributes.recommendationsPerformed}]`;
var lineKeyFor = (node) => {
  const line = node.closest(`[${cartPageAttributes.lineKey}]`);
  if (!(line instanceof HTMLElement))
    return null;
  const key = line.getAttribute(cartPageAttributes.lineKey);
  return key === null || key === "" ? null : key;
};

class CartPageV2 extends HTMLElement {
  isLoading = false;
  loadedProductId = null;
  sharedCartListener = null;
  restoreTimer = null;
  subscription = null;
  impressions = null;
  connectedCallback() {
    document.body.append(...this.querySelectorAll(`[${cartPageAttributes.shareModal}]`));
    this.paintWelcome();
    this.showRestoring(isReceivedSharedCart());
    this.sharedCartListener = () => {
      this.showRestoring(false);
      this.paintWelcome();
    };
    document.addEventListener("customCartLoaded", this.sharedCartListener);
    let isInitialNotify = true;
    this.subscription = cart.subscribe(() => {
      if (isInitialNotify)
        return;
      if (this.isLoading)
        return;
      this.refresh();
    });
    isInitialNotify = false;
    this.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement))
        return;
      const recoLink = target.closest("a");
      if (recoLink instanceof HTMLElement) {
        const tile = readRecoTile(recoLink);
        if (tile !== null)
          trackRecoSelect(tile);
      }
      const upgrade = target.closest(`[${upgradeAttributes.action}]`);
      if (upgrade instanceof HTMLElement) {
        event.preventDefault();
        this.runUpgrade(upgrade);
        return;
      }
      const undo = target.closest(`[${upgradeAttributes.undo}]`);
      if (undo instanceof HTMLElement) {
        event.preventDefault();
        this.runUndo(undo);
        return;
      }
      const quantityButton = target.closest(`[${cartPageAttributes.quantity}]`);
      if (quantityButton instanceof HTMLElement) {
        event.preventDefault();
        this.applyQuantity(quantityButton);
        return;
      }
      const addButton = target.closest(`[${cartPageAttributes.add}]`);
      if (addButton instanceof HTMLElement) {
        event.preventDefault();
        this.addRecommendation(addButton);
      }
    });
    this.addEventListener("keydown", arrowNavigate);
    this.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement))
        return;
      if (!target.matches(`[${cartPageAttributes.swap}]`))
        return;
      selectOnly(target);
      this.swapPaymentOption(target);
    });
    this.impressions = watchCartImpressions(this, "cart_page", null, () => true);
    this.loadRecommendations();
    if (!isReceivedSharedCart())
      this.reconcileHsf();
  }
  async reconcileHsf() {
    await cart.load();
    await reconcileHsfLines();
  }
  setIsLoading(isLoading) {
    this.isLoading = isLoading;
    this.setAttribute(cartPageAttributes.isLoading, String(isLoading));
  }
  async applyQuantity(button) {
    if (this.isLoading)
      return;
    const quantity = readInteger(button.getAttribute(cartPageAttributes.quantity));
    const lineKey = lineKeyFor(button);
    if (quantity === null || lineKey === null)
      return;
    if (quantity === 0 && !await confirmRemoval(lineKey))
      return;
    this.setIsLoading(true);
    clearCartError();
    showCartError(await setLineQuantity(lineKey, quantity));
    await this.refresh();
    this.setIsLoading(false);
  }
  async addRecommendation(button) {
    if (this.isLoading)
      return;
    const variantId = readInteger(button.getAttribute(cartPageAttributes.add));
    if (variantId === null)
      return;
    const sellingPlanId = readInteger(button.getAttribute(cartPageAttributes.addPlan));
    const mode = button.getAttribute(cartPageAttributes.addMode) === "installments" ? "installments" : "full";
    const properties = {
      _globalId: button.getAttribute(cartPageAttributes.addGlobalId) ?? "",
      _discountvalue: button.getAttribute(cartPageAttributes.addDiscount) ?? "",
      _selling_plan_name: "",
      _sellingplan: "",
      _ots_installment: "false"
    };
    if (sellingPlanId !== null)
      properties["_subscriptionType"] = subscriptionTypeFor(mode);
    const tile = readRecoTile(button);
    this.setIsLoading(true);
    clearCartError();
    const result = await addRecommendedLine({ variantId, sellingPlanId, properties }, tile?.listId ?? null);
    showCartError(result);
    if (result.ok && tile !== null)
      trackRecoAdd(tile);
    if (result.ok)
      await this.refresh();
    this.setIsLoading(false);
  }
  async runUpgrade(button) {
    if (this.isLoading)
      return;
    const lineKey = lineKeyFor(button);
    if (lineKey === null)
      return;
    const offer = readUpgradeOffer(button);
    this.setIsLoading(true);
    clearCartError();
    const result = await applyUpgrade(button, lineKey);
    showCartError(result);
    if (result.ok && offer !== null)
      trackPromotion("select_promotion", "cart_page", offer);
    await this.refresh();
    this.setIsLoading(false);
  }
  async runUndo(button) {
    if (this.isLoading)
      return;
    const lineKey = lineKeyFor(button);
    if (lineKey === null)
      return;
    const upgraded = readUpgradedLine(button);
    this.setIsLoading(true);
    clearCartError();
    const result = await undoUpgrade(button, lineKey);
    showCartError(result);
    if (result.ok && upgraded !== null)
      trackPromotion("upgrade_undo", "cart_page", upgraded);
    await this.refresh();
    this.setIsLoading(false);
  }
  disconnectedCallback() {
    this.impressions?.disconnect();
    this.impressions = null;
    if (this.restoreTimer !== null)
      window.clearTimeout(this.restoreTimer);
    this.restoreTimer = null;
    this.subscription?.unsubscribe();
    this.subscription = null;
    if (this.sharedCartListener === null)
      return;
    document.removeEventListener("customCartLoaded", this.sharedCartListener);
    this.sharedCartListener = null;
  }
  async swapPaymentOption(input) {
    if (this.isLoading)
      return;
    const variantId = readInteger(input.getAttribute(cartPageAttributes.swapVariant));
    const sellingPlanId = readInteger(input.getAttribute(cartPageAttributes.swapPlan));
    const cycleTotal = readInteger(input.getAttribute(cartPageAttributes.swapTotal));
    const lineKey = lineKeyFor(input);
    if (variantId === null || sellingPlanId === null || lineKey === null)
      return;
    this.setIsLoading(true);
    await cart.load();
    const existing = cart.line(lineKey);
    if (existing === null) {
      this.setIsLoading(false);
      return;
    }
    const twins = readHsfTwins(input.getAttribute(cartPageAttributes.swapChildren));
    const children = hsfChildLinesOf(existing);
    const swapMode = input.getAttribute(cartPageAttributes.swapMode);
    const subscriptionType = swapMode === "installments" || swapMode === "full" ? subscriptionTypeFor(swapMode) : null;
    const targetFor = (child) => twins.get(child.variantId) ?? {
      variantId: child.variantId,
      sellingPlanId: child.sellingPlanId,
      globalId: null
    };
    const properties = carriedProperties(existing);
    if (cycleTotal !== null) {
      properties["_monthly_base_total"] = (cycleTotal / 100).toFixed(2);
    }
    if (subscriptionType !== null) {
      properties["_subscriptionType"] = subscriptionType;
    }
    properties["_selling_plan_name"] = input.getAttribute(cartPageAttributes.swapPlanName) ?? "";
    const globalId = input.getAttribute(cartPageAttributes.swapGlobalId) ?? "";
    if (globalId !== "")
      properties["_globalId"] = globalId;
    if (children.length > 0) {
      properties["_childProductIds"] = children.map((child) => String(targetFor(child).variantId)).join(",");
    }
    const lines = [
      { variantId, quantity: existing.quantity, sellingPlanId, properties }
    ];
    for (const child of children) {
      const twin = targetFor(child);
      const childProperties = carriedProperties(child);
      childProperties["_adultProductIds"] = String(variantId);
      if (twin.globalId !== null)
        childProperties["_globalId"] = twin.globalId;
      if (subscriptionType !== null) {
        childProperties["_subscriptionType"] = subscriptionType;
      }
      lines.push({
        variantId: twin.variantId,
        quantity: child.quantity,
        sellingPlanId: twin.sellingPlanId,
        properties: childProperties
      });
    }
    showCartError(await swapLines([existing, ...children], lines));
    await this.refresh();
    this.setIsLoading(false);
  }
  showRestoring(restoring) {
    const overlay = document.querySelector(`[${cartPageAttributes.restoring}]`);
    if (!(overlay instanceof HTMLElement))
      return;
    overlay.classList.toggle("hidden", !restoring);
    if (this.restoreTimer !== null)
      window.clearTimeout(this.restoreTimer);
    this.restoreTimer = restoring ? window.setTimeout(() => {
      this.showRestoring(false);
    }, RESTORE_TIMEOUT_MS) : null;
  }
  paintWelcome() {
    const welcome = this.querySelector(`[${cartPageAttributes.welcome}]`);
    if (welcome instanceof HTMLElement)
      welcome.hidden = !isReceivedSharedCart();
  }
  async refresh() {
    const anchor = captureFocus(this);
    const parts = await fetchSectionParts(SECTION_ID, [
      COUNT_ID,
      MAIN_ID,
      RAIL_ID,
      WELCOME_ID,
      RECOMMENDATIONS_KEY_ID,
      RECOMMENDATIONS_OPTION_ID
    ]);
    if (parts === null)
      return;
    const main = this.querySelector(`[${cartPageAttributes.main}]`);
    const rail = this.querySelector(`[${cartPageAttributes.rail}]`);
    if (!(main instanceof HTMLElement) || !(rail instanceof HTMLElement))
      return;
    main.innerHTML = parts[MAIN_ID] ?? "";
    rail.innerHTML = parts[RAIL_ID] ?? "";
    const welcome = this.querySelector(`[${cartPageAttributes.welcome}]`);
    if (welcome instanceof HTMLElement)
      welcome.innerHTML = parts[WELCOME_ID] ?? "";
    this.paintWelcome();
    for (const node of this.querySelectorAll(`[${cartPageAttributes.count}]`)) {
      node.innerHTML = parts[COUNT_ID] ?? "";
    }
    anchor.restore();
    const host = this.querySelector("cart-page-v2-recommendations");
    if (host instanceof HTMLElement) {
      host.setAttribute(cartPageAttributes.productId, (parts[RECOMMENDATIONS_KEY_ID] ?? "").trim());
      host.setAttribute(cartPageAttributes.recoOption, (parts[RECOMMENDATIONS_OPTION_ID] ?? "").trim());
    }
    await this.loadRecommendations();
    anchor.restore();
  }
  async loadRecommendations() {
    const host = this.querySelector("cart-page-v2-recommendations");
    if (!(host instanceof HTMLElement))
      return;
    const productId = host.getAttribute(cartPageAttributes.productId) ?? "";
    if (productId === "") {
      host.innerHTML = "";
      this.loadedProductId = null;
      return;
    }
    const key = `${productId}|${host.getAttribute(cartPageAttributes.recoOption) ?? ""}`;
    if (key === this.loadedProductId)
      return;
    const markup = await fetchRecommendationsSection(RECOMMENDATIONS_SECTION_ID, productId, host.getAttribute(cartPageAttributes.limit) ?? "4", RECOMMENDATIONS_SELECTOR);
    host.innerHTML = markup ?? "";
    this.loadedProductId = key;
  }
}
var registerCartPageV2 = () => {
  if (customElements.get("cart-page-v2") === undefined) {
    customElements.define("cart-page-v2", CartPageV2);
  }
};

// src/pages/cart-v2/index.ts
registerCartPageV2();
disarmLegacyCartForm(cartPageAttributes.legacy);
